import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CreditCard, RotateCcw, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { getPendingCheckout, clearPendingCheckout, goToCheckout } from "@/lib/checkout";

// After a cancelled / abandoned Stripe payment: offer to resume the same session, or relaunch if it expired.
export default function PendingCheckoutBanner() {
  const { t } = useLang();
  const { pathname, search } = useLocation();
  const [state, setState] = useState(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    const p = getPendingCheckout();
    const flagged = /[?&](payment_cancelled|cancel)=1/.test(search);
    const samePage = p && p.returnTo.split("?")[0] === pathname;
    if (!p || (!samePage && !flagged) || pathname.startsWith("/payment/")) { setState(null); return; }
    let gone = false;
    setPending(p);
    api(`/checkout/session-state/${p.sid}`, { auth: false })
      .then((r) => {
        if (gone) return;
        if (r.status === "complete") { clearPendingCheckout(); setState(null); return; }
        setState(r.status === "open" ? { kind: "open", url: r.url || p.url } : { kind: "expired" });
      })
      .catch(() => {});
    return () => { gone = true; };
  }, [pathname, search]);

  if (!state || !pending) return null;
  const close = () => { clearPendingCheckout(); setState(null); };
  const relaunch = () => {
    clearPendingCheckout();
    setState(null);
    window.dispatchEvent(new CustomEvent("findloop:relaunch-payment"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const open = state.kind === "open";

  return (
    <div className="fixed z-[60] top-20 right-4 left-4 sm:left-auto sm:w-[380px] form-card bg-white rounded-3xl border border-border overflow-hidden animate-fade-up" data-testid="pending-checkout-banner" data-state={state.kind}>
      <div className="form-head bg-findo-navy text-white px-5 py-4 flex items-start gap-3">
        <span className="dlg-icon shrink-0">{open ? <CreditCard size={18} /> : <Clock size={18} />}</span>
        <div className="min-w-0 flex-1">
          <p className="font-display font-semibold leading-tight" data-testid="pending-checkout-title">{t(open ? "pay.pendingTitle" : "pay.expiredTitle")}</p>
          <p className="mt-1 text-xs text-white/70 leading-relaxed">{t(open ? "pay.pendingText" : "pay.expiredText")}</p>
        </div>
        <button type="button" onClick={close} className="text-white/70 hover:text-white rounded-full p-1" aria-label="close" data-testid="pending-checkout-close"><X size={16} /></button>
      </div>
      <div className="p-4">
        {open ? (
          <Button onClick={() => goToCheckout(state.url, { returnTo: pending.returnTo })} className="w-full h-11 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90 text-white" data-testid="pending-checkout-resume">
            <CreditCard size={16} className="mr-1.5" /> {t("pay.resume")}
          </Button>
        ) : (
          <Button onClick={relaunch} className="w-full h-11 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90 text-white" data-testid="pending-checkout-relaunch">
            <RotateCcw size={16} className="mr-1.5" /> {t("pay.relaunch")}
          </Button>
        )}
      </div>
    </div>
  );
}
